import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { createComponentLogger } from '@/lib/utils/logger';
import { Button } from '@/components/base';

const logger = createComponentLogger('EditAppointmentModal');
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/base/Form/Select';
import { Modal } from '@/components/base';
import { AppointmentRowData } from '@/components/views/DashboardView';
import { appointmentsApi, AppointmentStatus } from '@/lib/api/appointments';
import { medplumResourcesApi } from '@/lib/api/medplum-resources';
import { vitalsApi } from '@/lib/api/vitals';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from 'sonner';
import {
  getCurrentTimeString,
  getEndOfDayTimeString,
  getTimeStringFromDate,
  createDateFromDateAndTime,
  getTodayDateString,
  getDateString,
} from '@/lib/utils/date';
import { ChevronDown, ChevronUp, Activity } from 'lucide-react';
import type { VitalSigns } from '@/lib/types/consultation';

// Zod schema for vitals validation
const vitalsSchema = z.object({
  bloodPressure: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true;
        return /^\d{2,3}\/\d{2,3}$/.test(val.trim());
      },
      { message: 'Blood pressure must be in format Systolic/Diastolic (e.g., 120/80)' }
    )
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true;
        const match = val.trim().match(/^(\d+)\/(\d+)$/);
        if (!match) return false;
        const systolic = parseInt(match[1], 10);
        const diastolic = parseInt(match[2], 10);
        return (
          systolic > diastolic &&
          systolic >= 60 &&
          systolic <= 250 &&
          diastolic >= 40 &&
          diastolic <= 150
        );
      },
      { message: 'Blood pressure values must be medically reasonable' }
    ),
  heartRate: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 20 && val <= 300), {
      message: 'Heart rate must be between 20 and 300 bpm',
    }),
  temperature: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 80 && val <= 115), {
      message: 'Temperature must be between 80°F and 115°F',
    }),
  respiratoryRate: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 4 && val <= 80), {
      message: 'Respiratory rate must be between 4 and 80 breaths/min',
    }),
  oxygenSaturation: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 50 && val <= 100), {
      message: 'Oxygen saturation must be between 50% and 100%',
    }),
  bloodSugar: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 20 && val <= 1000), {
      message: 'Blood sugar must be between 20 and 1000 mg/dL',
    }),
  weight: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val > 0 && val <= 500), {
      message: 'Weight must be between 1 and 500 kg',
    }),
  height: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val > 0 && val <= 300), {
      message: 'Height must be between 1 and 300 cm',
    }),
});

// Initial empty vitals state
const EMPTY_VITALS: VitalSigns = {
  bloodPressure: '',
  heartRate: undefined,
  temperature: undefined,
  respiratoryRate: undefined,
  oxygenSaturation: undefined,
  bloodSugar: undefined,
  weight: undefined,
  height: undefined,
};

export interface EditAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentRowData | null;
  onAppointmentUpdated?: () => void;
}

export const EditAppointmentModal: React.FC<EditAppointmentModalProps> = ({
  isOpen,
  onClose,
  appointment,
  onAppointmentUpdated,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // Get current user for role-based visibility
  const currentUser = useAuthStore((state) => state.user);

  // Selected practitioner state
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string>('');

  // Fetch practitioners list
  const { data: practitionersData, isLoading: isLoadingPractitioners } = useQuery({
    queryKey: ['practitioners-for-appointment-edit'],
    queryFn: async () => {
      return medplumResourcesApi.listPractitioners({ count: 100 });
    },
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState(getCurrentTimeString);
  const [endTime, setEndTime] = useState(getEndOfDayTimeString);
  const [status, setStatus] = useState<AppointmentStatus>('booked');

  // Vitals state (editable)
  const [showVitals, setShowVitals] = useState(false);
  const [vitals, setVitals] = useState<VitalSigns>({ ...EMPTY_VITALS });
  const [originalVitals, setOriginalVitals] = useState<VitalSigns>({ ...EMPTY_VITALS });
  const [isLoadingVitals, setIsLoadingVitals] = useState(false);
  const [vitalsErrors, setVitalsErrors] = useState<Record<string, string>>({});

  // Check if any vitals are present
  const hasVitals =
    vitals &&
    ((vitals.bloodPressure && vitals.bloodPressure.trim() !== '') ||
      (vitals.heartRate !== undefined && !isNaN(vitals.heartRate)) ||
      (vitals.temperature !== undefined && !isNaN(vitals.temperature)) ||
      (vitals.respiratoryRate !== undefined && !isNaN(vitals.respiratoryRate)) ||
      (vitals.oxygenSaturation !== undefined && !isNaN(vitals.oxygenSaturation)) ||
      (vitals.bloodSugar !== undefined && !isNaN(vitals.bloodSugar)) ||
      (vitals.weight !== undefined && !isNaN(vitals.weight)) ||
      (vitals.height !== undefined && !isNaN(vitals.height)));

  // Check if vitals have been modified
  const vitalsModified = JSON.stringify(vitals) !== JSON.stringify(originalVitals);

  // Validate vitals using Zod schema
  const validateVitals = (): boolean => {
    if (!hasVitals) {
      setVitalsErrors({});
      return true;
    }

    const result = vitalsSchema.safeParse(vitals);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        errors[field] = err.message;
      });
      setVitalsErrors(errors);
      toast.error(result.error.errors[0].message);
      return false;
    }
    setVitalsErrors({});
    return true;
  };

  // Handle vitals field change
  const handleVitalChange = (field: keyof VitalSigns, value: string) => {
    setVitalsErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    if (field === 'bloodPressure') {
      setVitals((prev) => ({ ...prev, [field]: value }));
    } else {
      const numValue = value === '' ? undefined : parseFloat(value);
      setVitals((prev) => ({ ...prev, [field]: numValue }));
    }
  };

  // Initialize form data when appointment changes
  useEffect(() => {
    if (appointment) {
      setStatus(
        (appointment.originalStatus || appointment.status.toLowerCase()) as AppointmentStatus
      );
      const effectiveDate = appointment.appointmentStart
        ? getDateString(appointment.appointmentStart)
        : getTodayDateString();
      setDate(effectiveDate);
      setStartTime(getTimeStringFromDate(appointment.appointmentStart));
      setEndTime(
        appointment.appointmentEnd
          ? getTimeStringFromDate(appointment.appointmentEnd)
          : getEndOfDayTimeString()
      );

      // Set selected practitioner if available for administrator users.
      if (appointment.practitionerId) {
        setSelectedPractitionerId(appointment.practitionerId);
      }

      // Fetch vitals if appointment has an encounter
      if (appointment.encounterId) {
        setIsLoadingVitals(true);
        vitalsApi
          .getVitalsForEncounter(appointment.encounterId)
          .then((fetchedVitals) => {
            setVitals(fetchedVitals);
            setOriginalVitals(fetchedVitals);
          })
          .catch((error) => {
            logger.error('Failed to fetch vitals:', error);
            setVitals({ ...EMPTY_VITALS });
            setOriginalVitals({ ...EMPTY_VITALS });
          })
          .finally(() => {
            setIsLoadingVitals(false);
          });
      } else {
        setVitals({ ...EMPTY_VITALS });
        setOriginalVitals({ ...EMPTY_VITALS });
      }
    }
  }, [appointment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!appointment) return;

    // Validate date is not in the past
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error('Cannot set appointment to a past date');
      return;
    }

    // Validate vitals if modified
    if (vitalsModified && !validateVitals()) {
      return;
    }

    setIsLoading(true);

    // Track newly created resources for potential rollback
    let newlyCreatedEncounterId: string | null = null;
    let newlyCreatedObservationIds: string[] = [];

    try {
      // Create the start date from the selected date and time
      const newStartDate = createDateFromDateAndTime(date, startTime);

      // Create the end date from the selected date and end time
      const newEndDate = createDateFromDateAndTime(date, endTime);

      // Get authenticated user and validate
      const user = useAuthStore.getState().user;
      if (!user?.id) {
        toast.error('User not authenticated. Please log in again.');
        setIsLoading(false);
        return;
      }

      // Determine practitioner ID: use selected if available, otherwise existing appointment's practitioner
      const effectivePractitionerId =
        selectedPractitionerId || appointment.practitionerId || user.id;

      let encounterId = appointment.encounterId;

      // If vitals are being added/modified and we have vitals data
      if (vitalsModified && hasVitals) {
        // If no encounter exists, create one first
        if (!encounterId) {
          logger.log('Creating Encounter for appointment without existing encounter');

          const encounter = await medplumResourcesApi.createEncounter({
            patientId: `Patient/${appointment.patientId}`,
            practitionerId: `Practitioner/${effectivePractitionerId}`,
            status: 'planned',
            class: 'outpatient',
            type: appointment.visitType || 'Follow Up',
            period: {
              start: newStartDate.toISOString(),
            },
            appointmentId: appointment.id,
          });

          encounterId = encounter.id;
          newlyCreatedEncounterId = encounter.id; // Track for rollback
          logger.log('Encounter created:', encounterId);
        }

        // Save vitals linked to the encounter
        if (encounterId) {
          newlyCreatedObservationIds = await vitalsApi.saveVitals({
            patientId: appointment.patientId,
            encounterId,
            vitals,
            practitionerId: effectivePractitionerId,
          });
          logger.log(
            'Vitals saved for encounter:',
            encounterId,
            'Observation IDs:',
            newlyCreatedObservationIds
          );
        }
      }

      // Check if practitioner has changed
      const practitionerChanged =
        selectedPractitionerId && selectedPractitionerId !== appointment.practitionerId;

      // Update appointment (include encounterId if newly created, and practitionerId if changed)
      await appointmentsApi.updateAppointment(appointment.id, {
        status,
        start: newStartDate.toISOString(),
        end: newEndDate.toISOString(),
        // Link encounter if it was newly created
        ...(encounterId && !appointment.encounterId && { encounterId }),
        // Update practitioner if changed by an administrator.
        ...(practitionerChanged && { practitionerId: selectedPractitionerId }),
      });

      toast.success('Appointment updated successfully');
      onAppointmentUpdated?.();
      onClose();
    } catch (error) {
      logger.error('Failed to update appointment:', error);

      // Rollback: Delete newly created observations first (they reference the encounter)
      if (newlyCreatedObservationIds.length > 0) {
        logger.log('Rolling back: Deleting orphaned observations:', newlyCreatedObservationIds);
        for (const observationId of newlyCreatedObservationIds) {
          try {
            await medplumResourcesApi.deleteObservation(observationId);
            logger.log('Rollback successful: Observation deleted:', observationId);
          } catch (rollbackError) {
            // Log rollback failure but continue with other deletions
            logger.error(
              'Rollback failed: Could not delete observation:',
              observationId,
              rollbackError
            );
          }
        }
      }

      // Rollback: Delete newly created encounter if subsequent operations failed
      if (newlyCreatedEncounterId) {
        try {
          logger.log('Rolling back: Deleting orphaned encounter:', newlyCreatedEncounterId);
          await medplumResourcesApi.deleteEncounter(newlyCreatedEncounterId);
          logger.log('Rollback successful: Encounter deleted');
        } catch (rollbackError) {
          // Log rollback failure but don't throw - the original error is more important
          logger.error('Rollback failed: Could not delete encounter:', rollbackError);
        }
      }

      toast.error('Failed to update appointment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setVitalsErrors({});
      onClose();
    }
  };

  if (!appointment) return null;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      size="md"
      disableOutsideClick={true}
      footer={
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          loading={isLoading}
          className="w-full rounded-lg bg-[#061a61] py-2.5 text-sm font-medium hover:bg-[#061a61]/90"
          onClick={handleSubmit}
        >
          {isLoading ? 'Updating...' : 'Update Appointment'}
        </Button>
      }
    >
      <div
        className="scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 max-h-[65vh] overflow-y-auto pr-2"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db #f3f4f6' }}
      >
        {/* Centered Header */}
        <div className="mb-4 text-center">
          <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-gray-700">
            Update Appointment
          </h2>
          <p className="mx-auto mt-2 w-3/4 font-hanken text-base font-normal leading-label-2 tracking-label-2 text-gray-700">
            Update appointment details for {appointment.patientName}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Patient Info - Read only */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Patient
            </label>
            <div className="flex h-10 w-full items-center rounded-lg bg-[#fbfafd] px-3 ring-1 ring-inset ring-gray-200">
              <span className="text-sm text-gray-900">{appointment.patientName}</span>
            </div>
          </div>

          {/* Practitioner Dropdown */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Assigned Practitioner
            </label>
            <Select
              value={selectedPractitionerId}
              onValueChange={setSelectedPractitionerId}
              disabled={isLoading || isLoadingPractitioners}
            >
              <SelectTrigger className="h-10 w-full rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-[#061a61]">
                <SelectValue
                  placeholder={
                    isLoadingPractitioners ? 'Loading practitioners...' : 'Select a practitioner'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {practitionersData?.practitioners?.map((practitioner: any) => {
                  const name = practitioner.name?.[0];
                  const displayName = name
                    ? `${name.prefix?.[0] ? name.prefix[0] + ' ' : ''}${name.given?.join(' ') || ''} ${name.family || ''}`.trim()
                    : practitioner.id || 'Unknown';
                  return (
                    <SelectItem key={practitioner.id} value={practitioner.id || ''}>
                      {displayName}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Visit Type - Read only */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Visit Type
            </label>
            <div className="flex h-10 w-full items-center rounded-lg bg-[#fbfafd] px-3 ring-1 ring-inset ring-gray-200">
              <span className="text-sm text-gray-900">{appointment.visitType}</span>
            </div>
          </div>

          {/* Date Field - Editable */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              disabled={isLoading}
              className="flex h-10 w-full items-center rounded-lg bg-[#fbfafd] px-3 font-hanken text-sm ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-[#061a61]/20"
            />
          </div>

          {/* Time Fields - Start and End */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block font-hanken text-sm font-medium text-gray-700">
                Start Time
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={isLoading}
                className="flex h-10 w-full items-center rounded-lg bg-[#fbfafd] px-3 font-hanken text-sm ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-[#061a61]/20"
              />
            </div>
            <div>
              <label className="mb-2 block font-hanken text-sm font-medium text-gray-700">
                End Time
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={isLoading}
                className="flex h-10 w-full items-center rounded-lg bg-[#fbfafd] px-3 font-hanken text-sm ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-[#061a61]/20"
              />
            </div>
          </div>

          {/* Status Field - Editable */}
          <div>
            <label className="mb-2 block font-hanken text-sm font-medium text-gray-700">
              Status
            </label>
            <Select
              value={status}
              onValueChange={(value: AppointmentStatus) => setStatus(value)}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="booked">Booked</SelectItem>
                <SelectItem value="arrived">Arrived</SelectItem>
                <SelectItem value="fulfilled">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="noshow">No Show</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Vitals Section - Always Editable (hidden for front desk role) */}
          {currentUser?.role !== 'frontdesk' && (
            <div className="border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => setShowVitals(!showVitals)}
                className="flex w-full items-center justify-between rounded-lg bg-[#f8f9fc] px-4 py-3 text-left transition-colors hover:bg-[#f0f2f8]"
                disabled={isLoading}
              >
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-[#061a61]" />
                  <span className="font-hanken text-sm font-medium text-gray-700">
                    {appointment?.encounterId ? 'Edit Vitals' : 'Add Vitals'}
                  </span>
                  {isLoadingVitals && <span className="text-xs text-gray-400">Loading...</span>}
                  {!isLoadingVitals && hasVitals && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                      Recorded
                    </span>
                  )}
                  {!isLoadingVitals && vitalsModified && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                      Modified
                    </span>
                  )}
                </div>
                {showVitals ? (
                  <ChevronUp className="h-4 w-4 text-gray-700" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-700" />
                )}
              </button>

              {showVitals && !isLoadingVitals && (
                <div className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-white p-3">
                  <p className="text-xs text-gray-500">
                    {appointment?.encounterId
                      ? 'Update vitals for this appointment. All fields are optional.'
                      : 'Add vitals for this appointment. All fields are optional.'}
                  </p>

                  {/* Row 1: Blood Pressure, Heart Rate, Temperature, Respiratory Rate */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.bloodPressure ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        BP (mmHg)
                      </label>
                      <input
                        type="text"
                        placeholder="120/80"
                        value={vitals.bloodPressure || ''}
                        onChange={(e) => handleVitalChange('bloodPressure', e.target.value)}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.bloodPressure ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.heartRate ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        HR (bpm)
                      </label>
                      <input
                        type="number"
                        placeholder="72"
                        value={vitals.heartRate ?? ''}
                        onChange={(e) => handleVitalChange('heartRate', e.target.value)}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.heartRate ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.temperature ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        Temp (°F)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="98.6"
                        value={vitals.temperature ?? ''}
                        onChange={(e) => handleVitalChange('temperature', e.target.value)}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.temperature ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.respiratoryRate ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        RR (/min)
                      </label>
                      <input
                        type="number"
                        placeholder="16"
                        value={vitals.respiratoryRate ?? ''}
                        onChange={(e) => handleVitalChange('respiratoryRate', e.target.value)}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.respiratoryRate ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                    </div>
                  </div>

                  {/* Row 2: SpO2, Blood Sugar, Weight, Height */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.oxygenSaturation ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        SpO2 (%)
                      </label>
                      <input
                        type="number"
                        placeholder="98"
                        value={vitals.oxygenSaturation ?? ''}
                        onChange={(e) => handleVitalChange('oxygenSaturation', e.target.value)}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.oxygenSaturation ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.bloodSugar ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        Sugar (mg/dL)
                      </label>
                      <input
                        type="number"
                        placeholder="100"
                        value={vitals.bloodSugar ?? ''}
                        onChange={(e) => handleVitalChange('bloodSugar', e.target.value)}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.bloodSugar ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.weight ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        Weight (kg)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="70"
                        value={vitals.weight ?? ''}
                        onChange={(e) => handleVitalChange('weight', e.target.value)}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.weight ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.height ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        Height (cm)
                      </label>
                      <input
                        type="number"
                        placeholder="175"
                        value={vitals.height ?? ''}
                        onChange={(e) => handleVitalChange('height', e.target.value)}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.height ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
};
